import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ClickableText } from 'ClickableText';
import { ShowMoreCollapse } from './ShowMoreCollapse';
import { type StreamForStreamDashboard, type LessonForStreamDashboard } from 'Lessons';
import { getChapterLessonsIndex } from '../selectors';
import { type LessonKeyTerms } from '../StreamDashboard.types';
import { BandSectionHeading } from './BandSectionHeading';
import '../locales/streamDashboard/streamDashboard-en.json';

/*
    Key terms, grouped by lesson. Terms keep the lesson order; the legacy orderBy had no predicate and did
    not sort. No horizontal padding: the parent .dashboard-container supplies it.
*/

type Props = {
    stream: StreamForStreamDashboard;
    keyTermsByLesson: LessonKeyTerms[];
    isCollapsed: boolean;
    onShowAll: () => void;
    onKeyTermClick: (lesson: LessonForStreamDashboard) => void;
    brandName: string;
};

export const KeyTermsSection: FC<Props> = ({
    stream,
    keyTermsByLesson,
    isCollapsed,
    onShowAll,
    onKeyTermClick,
    brandName,
}) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'streamDashboard.streamDashboard' });

    return (
        <section className="pt-12.5">
            <BandSectionHeading
                id="key-terms-caption"
                caption={t('key_terms')}
                intro={t('key_terms_description', { brandName })}
            />

            {/* 50px below the description on mobile and 43px from sm up: the description's own margin plus
                the padding here. Padding rather than margin, because adjacent margins collapse and would
                leave only the larger of the two. The legacy markup got away with margins because the row
                holding the description is a flex container.

                36px against the legacy 35px on mobile, which is the nearest step. */}
            <ShowMoreCollapse collapsed={isCollapsed} onShowMore={onShowAll}>
                <div className="pt-9 sm:pt-6.25">
                    {keyTermsByLesson.map(({ chapterIndex, lesson, keyTerms }) => (
                        <div key={lesson.id} className="mb-7.5 last:mb-0">
                            {/* .lesson-index-box is a block, so the chapter and lesson numbers take their own
                                line and the title follows underneath. */}
                            <div className="mb-3.5">
                                <div className="text-xs font-semibold text-slate-grey-500">
                                    <span>{t('chapter_number_x', { number: chapterIndex + 1 })}</span>
                                    <span className="px-[3px]" aria-hidden>
                                        |
                                    </span>
                                    <span>
                                        {t('lesson_number_x', {
                                            number: (getChapterLessonsIndex(stream, lesson) ?? 0) + 1,
                                        })}
                                    </span>
                                </div>
                                <span className="text-lg font-medium text-slate-grey">
                                    {lesson.title.toUpperCase()}
                                </span>
                            </div>

                            <dl className="m-0">
                                {keyTerms.map(keyTerm => (
                                    <div key={keyTerm.keyTerm} className="mb-4.5">
                                        <dt className="mb-1.5">
                                            <ClickableText
                                                onPress={() => onKeyTermClick(lesson)}
                                                color="primary"
                                                underline="none"
                                                weight="semibold"
                                                allowWrap
                                                className="inline-flex items-center rounded-2 px-1.5 py-2 text-start text-md hover:bg-slate-grey-50"
                                            >
                                                {keyTerm.keyTerm.toUpperCase()}
                                            </ClickableText>
                                        </dt>
                                        {keyTerm.verified && (
                                            <dd className="ms-9.5 text-md text-slate-grey">{keyTerm.definition}</dd>
                                        )}
                                    </div>
                                ))}
                            </dl>
                        </div>
                    ))}
                </div>
            </ShowMoreCollapse>
        </section>
    );
};
